import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useGet } from 'hooks/useGet';
import { useAuthContext } from './AuthContext';

export const ApiContext = createContext();

export const ApiProvider = ({ children }) => {
  const { getData } = useGet();
  const { paCode }  = useAuthContext();
  const { bearerToken } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Dedicated loading / error state for the purge PA-codes fetch
  const [purgePaCodesLoading, setPurgePaCodesLoading] = useState(false);
  const [purgePaCodesError, setPurgePaCodesError] = useState(null);
  //"30218";
  const dealerCode=paCode;

  const [apiResponse, setApiResponse] = useState(() => ({
    mplCodes: JSON.parse(localStorage.getItem('mplCodes')) || [],
    mliCodes: JSON.parse(localStorage.getItem('mliCodes')) || [],
    paCodes: JSON.parse(localStorage.getItem('paCodes')) || [],
    parentPaCodes: JSON.parse(localStorage.getItem('parentPaCodes')) || [],
    certifiedPaCodes: JSON.parse(localStorage.getItem('certifiedPaCodes')) || [],
    purgePaCodes: JSON.parse(localStorage.getItem('purgePaCodes')) || [],
  }));

  const isDataLoaded = Object.values(apiResponse).every((arr) => arr.length > 0);

  // CWE-639 FIX: Validate dealerCode parameter to prevent authorization bypass
  const isValidDealerCode = (code) => {
    if (!code) return false;
    // Only allow alphanumeric characters, hyphens, and underscores
    return /^[a-zA-Z0-9_-]+$/.test(code);
  };

  const fetchCertifiedPaCodes = useCallback(async () => {
    try {
      //const baseUrl = process.env.REACT_APP_FADSRS_API_URL || `https://fadsrs-dcds-api-dev-793675442456.us-central1.run.app/fadsrs`;
      const baseUrl = process.env.REACT_APP_FADSRS_API_URL;

      //console.log("baseUrl ::"+baseUrl);

      localStorage.removeItem('certifiedPaCodes');

      // Fetch only the certified PA codes
      const certifiedPaResponse = await getData(`${baseUrl}/admin/cert/pacodes`);

      // Update only the certifiedPaCodes in the state
      setApiResponse(prev => ({
        ...prev,
        certifiedPaCodes: certifiedPaResponse?.result || []
      }));

      // Update localStorage for certifiedPaCodes
      localStorage.setItem('certifiedPaCodes', JSON.stringify(certifiedPaResponse?.result || []));

    } catch (error) {
      setError(error);
      console.error('Error fetching certified PA codes:', error);
    }
  }, [getData]);

   const fetchPurgePaCodes = useCallback(async () => {
      try {
        setPurgePaCodesLoading(true);
        setPurgePaCodesError(null);
        const baseUrl = process.env.REACT_APP_FADSRS_API_URL;

        localStorage.removeItem('purgePaCodes');

        const purgePaCodeResponse = await getData(`${baseUrl}/admin/purge/pacodes`);

        // Accept both { result: [...] } and { data: [...] } shapes; fall back to []
        let purgeCodes = [];
        if (purgePaCodeResponse && Array.isArray(purgePaCodeResponse.result)) {
          purgeCodes = purgePaCodeResponse.result;
        } else if (purgePaCodeResponse && Array.isArray(purgePaCodeResponse.data)) {
          purgeCodes = purgePaCodeResponse.data;
        } else if (Array.isArray(purgePaCodeResponse)) {
          purgeCodes = purgePaCodeResponse;
        }
        // else: empty / unexpected format — keep purgeCodes as []

        setApiResponse(prev => ({ ...prev, purgePaCodes: purgeCodes }));
        localStorage.setItem('purgePaCodes', JSON.stringify(purgeCodes));

      } catch (err) {
        setError(err);
        setPurgePaCodesError(err);
        console.error('Error fetching purge PA codes:', err);
      } finally {
        setPurgePaCodesLoading(false);
      }
    }, [getData]);

  const fetchData = useCallback(async () => {

// 1. Add a guard clause: If no token, don't attempt the fetch
    if (!bearerToken) return;

    try {
      setLoading(true);

      //const baseUrl = process.env.REACT_APP_FADSRS_API_URL || `https://fadsrs-dcds-api-dev-793675442456.us-central1.run.app/fadsrs`;
      const baseUrl = process.env.REACT_APP_FADSRS_API_URL;

      localStorage.removeItem('mplCodes');
      localStorage.removeItem('mliCodes');
      localStorage.removeItem('paCodes');
      localStorage.removeItem('parentPaCodes');
      localStorage.removeItem('certifiedPaCodes');
      localStorage.removeItem('purgePaCodes');

      // CWE-639 FIX: Validate dealerCode before using in URL parameters
      const validatedDealerCode = isValidDealerCode(dealerCode) ? dealerCode : null;

      const dealerPaUrl = validatedDealerCode !== null && validatedDealerCode !== undefined && validatedDealerCode !== ''
          ? `${baseUrl}/fadpacodes?dealerCode=${encodeURIComponent(validatedDealerCode)}`
          : `${baseUrl}/pacodes`;

      const parentDealerPaUrl = validatedDealerCode !== null && validatedDealerCode !== undefined && validatedDealerCode !== ''
          ? `${baseUrl}/fadpacodes?dealerCode=${encodeURIComponent(validatedDealerCode)}`
          : `${baseUrl}/parent/pacodes`;



      const [mplResponse, mliResponse, paResponse, parentPaResponse, certifiedPaResponse, purgePaResponse] = await Promise.all([
        getData(`${baseUrl}/mpl`),
        getData(`${baseUrl}/mli`),
        getData(dealerPaUrl),
        getData(parentDealerPaUrl),
        getData(`${baseUrl}/admin/cert/pacodes`),
        getData(`${baseUrl}/admin/purge/pacodes`)
      ]);

      const newApiResponse = {
        mplCodes: mplResponse?.result || [],
        mliCodes: mliResponse?.result || [],
        paCodes: paResponse?.result || [],
        parentPaCodes: parentPaResponse?.result || [],
        certifiedPaCodes: certifiedPaResponse?.result || [],
        purgePaCodes: purgePaResponse?.result || [],
      };

      setApiResponse(newApiResponse);

      Object.entries(newApiResponse).forEach(([key, value]) => {
        localStorage.setItem(key, JSON.stringify(value));
      });

      setLoading(false);
    } catch (error) {
      setError(error);
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [getData, bearerToken]);

  useEffect(() => {
    if (!isDataLoaded) {
      fetchData();
    }
  }, [isDataLoaded, fetchData, bearerToken]);

  return (
      <ApiContext.Provider value={{ apiResponse, loading, error, fetchData, fetchCertifiedPaCodes, fetchPurgePaCodes, purgePaCodesLoading, purgePaCodesError }}>
        {children}
      </ApiContext.Provider>
  );
};

export const useApi = () => useContext(ApiContext);
